import React, { Component } from 'react'
import styled, { withTheme } from 'styled-components'
import { withRouter } from 'react-router-dom'
import { connect } from 'react-redux'
import { Modal } from 'antd'
import { ExclamationCircleOutlined } from '@ant-design/icons'

import { Flex, FlexColumn } from '../../Reusable/Container'
import { Tag, Text } from '../../Reusable/Text'
import { getDateInLocalTZ, getUserApps, capitalizeFirstLetter, getLogoFromDomain } from '../../../Utils/util'
import More from "../../../Images/more.png"
import TableView from '../../Reusable/TableView'
import moment from 'moment'
import { ImageButton, TextButton } from '../../Reusable/Button'
import { Dropdown, Menu } from '../../Reusable/Dropdown'
import { DeleteOutlined } from '@ant-design/icons'
import hexToRgba from 'hex-to-rgba'
import { apiPOSTReq } from '../../../Utils/api'
import environment from '../../../environment'
import { ErrorAlert } from '../../Reusable/Alert'
import SearchFilter from './SearchFilter' // We'll create this as a simplified version of SearchAndFilter
import { InitialCircle } from '../../Reusable/InitialCircle'

const { confirm } = Modal

const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
})

class BillTable extends Component {
    constructor(props) {
        super(props)
        this.state = {
            bills: props.bills || [],
            loading: false,
            pagination: props.pagination || {
                current: 1,
                pageSize: 50,
                total: 0
            },
            sortFieldName: null,
            sortDirection: null,
            searchTerm: props.searchTerm || '',
            vendorId: props.vendorId || null,
            fromDate: props.fromDate || null,
            toDate: props.toDate || null,
            fromAmount: props.fromAmount || null,
            toAmount: props.toAmount || null,
            status: props.status || null, // New filter for status
            activeFilters: {} // Track active filters for UI display
        }
        
        // Add flag to track if fetchBills is in progress
        this._isFetchingBills = false;
    }

    componentDidMount() {
        // Apply any preset filters if provided
        this.applyPresetFiltersIfProvided();
        
        // If no bills provided or if vendorId is provided, fetch bills
        if (!this.props.bills || this.props.vendorId) {
            this.fetchBills();
        }
    }

    componentDidUpdate(prevProps) {
        // Fix: If a fetch is already in progress, return early
        if (this._isFetchingBills) return;
        
        const shouldRefetch = 
            prevProps.activeTab !== this.props.activeTab ||
            prevProps.vendorId !== this.props.vendorId ||
            prevProps.searchTerm !== this.props.searchTerm ||
            prevProps.fromDate !== this.props.fromDate ||
            prevProps.toDate !== this.props.toDate ||
            prevProps.fromAmount !== this.props.fromAmount ||
            prevProps.toAmount !== this.props.toAmount ||
            prevProps.status !== this.props.status ||
            prevProps.presetFilter !== this.props.presetFilter;

        // Check if preset filter has changed
        if (prevProps.presetFilter !== this.props.presetFilter && this.props.presetFilter) {
            this.applyPresetFilter(this.props.presetFilter);
            return; // Return early as applyPresetFilter will trigger fetchBills
        }

        if (shouldRefetch) {
            // Set flag to prevent multiple fetches
            this._isFetchingBills = true;
            
            this.setState({
                searchTerm: this.props.searchTerm,
                vendorId: this.props.vendorId,
                fromDate: this.props.fromDate,
                toDate: this.props.toDate,
                fromAmount: this.props.fromAmount,
                toAmount: this.props.toAmount,
                status: this.props.status
            }, () => {
                this.fetchBills();
                // Reset flag after fetch is scheduled
                this._isFetchingBills = false;
            });
        }

        // If bills are passed in from parent and changed
        if (this.props.bills && prevProps.bills !== this.props.bills) {
            this.setState({ bills: this.props.bills });
        }
    }

    applyPresetFiltersIfProvided = () => {
        // Apply preset filter if provided in props
        if (this.props.presetFilter) {
            this.applyPresetFilter(this.props.presetFilter);
        }
    }

    fetchBills = () => {
        const { 
            pagination, 
            sortFieldName, 
            sortDirection, 
            searchTerm, 
            vendorId, 
            fromDate, 
            toDate, 
            fromAmount, 
            toAmount,
            status 
        } = this.state;
        const { activeTab } = this.props;

        this.setState({ loading: true });

        const body = {
            "size": pagination.pageSize || 10,
            "page": (pagination.current || 1) - 1,
            "sortFieldName": sortFieldName || "AuditData.LastUpdatedTime",
            "sortDirection": sortDirection || "DESC"
        };

        // Add vendorId filter if provided
        if (vendorId) {
            body.vendorId = vendorId;
        }

        // Status filter handling - priority: explicit status filter > activeTab
        if (status) {
            // Fixed: Changed from "FILTER_BY_STATUS" to "FILTER_STATUS"
            body.viewby = "FILTER_STATUS"; 
            body.status = status;
            
            console.log('Using status filter in API request:', status);
        } else if (activeTab && activeTab !== 'All') {
            body.viewby = "FILTER_STATUS";
            body.status = activeTab;
            
            console.log('Using activeTab for status filter:', activeTab);
        } else {
            body.viewby = "FILTER_ALL";
        }

        // Add search term if provided
        if (searchTerm) {
            body.searchStr = searchTerm;
        }

        // Add date range if provided
        if (fromDate && toDate) {
            body.fromDate = fromDate;
            body.toDate = toDate;
        }

        // Add amount range if provided
        if (typeof fromAmount === 'number' && typeof toAmount === 'number') {
            body.fromAmount = fromAmount;
            body.toAmount = toAmount;
        }
        
        // Log the full request for debugging
        console.log('API Request:', JSON.stringify(body, null, 2));

        apiPOSTReq(`${environment.payBaseUrl}/payables/bills/getBills`, 
            { "BankProvider": environment.bankProvider.crb }, 
            body, 
            (err, resp) => {
                try {
                    const data = resp;
                    if (data.result) {
                        const newPagination = {
                            ...pagination,
                            total: data.count
                        };
                        console.log("newPagination", newPagination);
                        this.setState({
                            bills: data.bills || [],
                            loading: false,
                            pagination: newPagination
                        });

                        // If parent component needs to be notified of data updates
                        if (this.props.onDataLoaded) {
                            this.props.onDataLoaded(data.bills || [], newPagination);
                        }
                    } else {
                        throw Error(data.error || data.responseMessage || 
                            "Sorry we had trouble processing your request. Please try again later");
                    }
                } catch (error) {
                    console.log("ERROR /payables/bills/getBills", error, err, resp);
                    ErrorAlert({ description: error.message });
                    this.setState({ loading: false });
                } finally {
                    // Always reset the flag when API request completes
                    this._isFetchingBills = false;
                }
            }
        );
    }

    handleTableChange = (pagination, filters, sorter) => {
        console.log("Table change detected:", { pagination, sorter });
        let sortFieldName = this.state.sortFieldName;
        let sortDirection = this.state.sortDirection;

        if (sorter && sorter.order) {
            sortDirection = sorter.order === 'ascend' ? 'ASC' : 'DESC';

            if (sorter.field) {
                if (sorter.field === 'auditData') {
                    sortFieldName = 'AuditData.LastUpdatedTime';
                } else {
                    // Convert field to PascalCase as required by the API
                    sortFieldName = capitalizeFirstLetter(sorter.field);
                }
            }
            
            console.log("Sorting applied:", { sortFieldName, sortDirection });
        }

        // Set flag to prevent multiple fetches
        this._isFetchingBills = true;
        
        // Update state
        this.setState({
            pagination,
            sortFieldName,
            sortDirection
        }, () => {
            // Always fetch with new parameters when sorting changes
            this.fetchBills();
            
            // If parent handles table changes
            if (this.props.handleTableChange) {
                this.props.handleTableChange(pagination, filters, sorter);
            }
        });
    }

    handlePayBill = (bill) => {
        // If a custom handler is provided, use that instead
        if (this.props.handleOnRow) {
            this.props.handleOnRow(bill);
            return;
        }

        // Default implementation
        if (bill.vendorId) {
            this.props.history.push({
                pathname: `/payables/bills/view/${bill?.id}`,
                state: { bill, payFlow: true }
            });
        } else {
            // Create a callback for the confirmation dialog
            const callback = () => {
                // Get categories from props or store if needed
                let category;
                if (bill.coaid && this.props.categories) {
                    category = this.props.categories.find(obj => obj.id === bill.coaid);
                }

                this.props.history.push({
                    pathname: '/payables/bills/edit',
                    state: { bill, category, edit: true }
                });
            };

            // Show a confirmation dialog for bills without vendors
            confirm({
                title: 'No vendor',
                icon: <ExclamationCircleOutlined />,
                content: "You haven't set a vendor for this bill. Click 'OK' to edit your bill and set one now.",
                onOk() {
                    callback();
                },
                onCancel() {},
            });
        }
    }
    
    // New method to handle filter changes from SearchFilter component
    handleFilterChange = (filters) => {
        // Set flag to prevent multiple fetches
        this._isFetchingBills = true;
        
        // Create a new activeFilters object
        const newActiveFilters = { ...this.state.activeFilters };
        
        // Handle special case: if vendorId was provided as a prop, don't track it in activeFilters
        // This prevents "Clear all" from showing when only the required vendorId filter is present
        if (this.props.vendorId && !Object.keys(newActiveFilters).some(k => k !== 'vendorId')) {
            delete newActiveFilters.vendorId;
        }
        
        // Process each filter
        Object.keys(filters).forEach(key => {
            if (filters[key] === null || filters[key] === undefined || filters[key] === '') {
                // If filter is being removed, delete it from activeFilters
                delete newActiveFilters[key];
            } else {
                // Otherwise add/update it
                newActiveFilters[key] = filters[key];
            }
        });
        
        // Prepare new state with filters
        const newState = {
            ...this.state,
            pagination: {
                ...this.state.pagination,
                current: 1
            },
            activeFilters: newActiveFilters
        };
        
        // Update specific filter values
        if ('searchTerm' in filters) newState.searchTerm = filters.searchTerm || '';
        if ('fromDate' in filters) newState.fromDate = filters.fromDate;
        if ('toDate' in filters) newState.toDate = filters.toDate;
        if ('fromAmount' in filters) newState.fromAmount = filters.fromAmount;
        if ('toAmount' in filters) newState.toAmount = filters.toAmount;
        if ('status' in filters) newState.status = filters.status;
        
        // Preserve vendorId if it was provided as a prop
        if (this.props.vendorId) {
            newState.vendorId = this.props.vendorId;
        }
        
        // Create a simplified version of filters to pass to parent if needed
        const parentFilters = { ...filters };
        
        // Update state and fetch bills with new filters
        this.setState(newState, () => {
            this.fetchBills();
            
            // Notify parent if callback provided - with filtered information
            // to prevent circular updates
            if (this.props.onFilterChange) {
                this.props.onFilterChange(parentFilters);
            }
        });
    }
    
    // Method to clear all filters
    clearAllFilters = () => {
        console.log("Clearing all filters");
        
        // Set flag to prevent multiple fetches
        this._isFetchingBills = true;
        
        // Create a state update that preserves vendorId if it was set as a prop
        const stateUpdate = {
            searchTerm: '',
            fromDate: null,
            toDate: null,
            fromAmount: null,
            toAmount: null,
            status: null,
            activeFilters: {},
            pagination: {
                ...this.state.pagination,
                current: 1
            }
        };
        
        // Preserve vendorId if it was provided as a prop
        if (this.props.vendorId) {
            stateUpdate.vendorId = this.props.vendorId;
            // Don't add to activeFilters to avoid showing "Clear all" when only vendorId is present
        }
        
        this.setState(stateUpdate, () => {
            this.fetchBills();
            
            // Notify parent component if callback provided
            if (this.props.onFilterCleared) {
                this.props.onFilterCleared();
            }
        });
    }
    
    // Method to apply preset filters
    applyPresetFilter = (presetName) => {
        // Set flag to prevent multiple fetches
        this._isFetchingBills = true;
        
        let filterUpdate = {};
        
        switch(presetName) {
            case 'outstandingBills':
                filterUpdate = {
                    status: 'Unpaid',
                    fromAmount: 0.01 // Any positive balance
                };
                break;
                
            case 'overdueBills':
                // Set status to unpaid and due date before today
                const today = moment().format('YYYY-MM-DD');
                filterUpdate = {
                    status: 'Unpaid',
                    toDate: today
                };
                break;
                
            case 'scheduled':
                filterUpdate = {
                    status: 'Scheduled'
                };
                break;
                
            case 'recent':
                // Bills from last 30 days
                const thirtyDaysAgo = moment().subtract(30, 'days').format('YYYY-MM-DD');
                filterUpdate = {
                    fromDate: thirtyDaysAgo
                };
                break;
                
            default:
                this._isFetchingBills = false; // Reset flag if no valid preset
                return;
        }
        
        // Apply the filter updates
        this.setState({
            ...filterUpdate,
            pagination: {
                ...this.state.pagination,
                current: 1
            },
            activeFilters: {
                ...this.state.activeFilters,
                ...filterUpdate
            }
        }, this.fetchBills);
    }

    getColumns = () => {
        var { activeTab, aionStore, hideAction, hideColumns, theme, hideVendorColumn, customBillTag } = this.props;
        const { sortFieldName, sortDirection } = this.state;

        customBillTag = customBillTag || {};
                
        const userApps = getUserApps(aionStore);
        const { CurrentSync } = aionStore;
        let qbo = false;
        if (CurrentSync && CurrentSync.platformName === 'QUICKBOOKS' && 
            CurrentSync.syncSetupCompleted && userApps && userApps.includes("Payables")) {
            qbo = true;
        }

        var columns = [];
        
        // Only add Vendor column if not hidden
        if (!hideVendorColumn) {
            columns.push({
                title: <Text weight='500' noWrap>Vendor</Text>,
                dataIndex: 'vendorName',
                key: 'vendorName',
                sorter: true,
                showSorterTooltip: false,
                width: '500px',
                render: (vendorName, record) => {
                    return (
                        <Flex start centerHorizontally gap='8px'>
                            <div>
                                <InitialCircle size={30} name={vendorName} useFirstWordOnly={true} /*logoUrl={getLogoFromDomain(record.sender)}*/ />
                            </div>
                            
                            <FlexColumn start gap='2px'>
                                <Text weight={500} color={theme.colors.primary2}>{vendorName}</Text>

                                <Flex centerH start gap='8px'>
                                    <Text size={'14px'} height={'14px'} weight={500} color={hexToRgba(this.props.theme.colors.primary2, 0.5)}>
                                        #{record.invoiceNumber}
                                    </Text>
                                    {
                                        customBillTag[record.invoiceNumber] && 
                                        <Text size={'14px'} height={'14px'} weight={500} color={hexToRgba(this.props.theme.colors.primary2, 0.5)}>
                                            | {customBillTag[record.invoiceNumber]}
                                        </Text>
                                    }
                                </Flex>
                                
                            </FlexColumn>
                        </Flex>
                    )
                }
            });
        }

        if (hideVendorColumn) {
            columns.push({
                title: <Text weight='500' noWrap>No.</Text>,
                dataIndex: 'invoiceNumber',
                key: 'invoiceNumber',
                width: "280px",
                sorter: true,
                showSorterTooltip: false,
                render: (invoiceNumber, bill) => {
                    return (
                        <Flex start centerH gap='4px'>
                            <Text weight='500' noWrap>{invoiceNumber}</Text>
                            {customBillTag[invoiceNumber] && <Tag>{customBillTag[invoiceNumber]}</Tag>}
                        </Flex>
                            
                    )
                }
            })
        }

        columns.push({
            title: <Text weight='500' noWrap>Balance</Text>,
            dataIndex: 'balance',
            key: 'balance',
            render: (balance, bill) => {
                return (
                    <Flex start className='cell-content'>
                        <Text weight='500'>
                            {formatter.format(balance)}
                            <span className='hover-content' style={{ color: hexToRgba(theme.colors.primary, 0.7) }}> 
                                / <b title='Total'>{formatter.format(bill.amount)}</b>
                            </span>
                        </Text>
                    </Flex>
                );
            },
            align: 'left',
            width: "220px",
            sorter: true,
            showSorterTooltip: false,
        });

        columns.push(
            // {
            //     title: <Text weight='500' noWrap>No.</Text>,
            //     dataIndex: 'invoiceNumber',
            //     key: 'invoiceNumber',
            //     width: "280px",
            //     sorter: true,
            //     showSorterTooltip: false
            // },
            {
                title: <Text weight='500' noWrap>Due Date</Text>,
                dataIndex: 'dueDate',
                key: 'dueDate',
                sorter: true,
                showSorterTooltip: false,
                align: 'right',
                render: (date, record) => {
                    if (date) {
                        date = new Date(date).toISOString();
                        date = date.split("T")[0];
                    }
                    return(
                        <Flex centerH end gap='8px'>
                            <Tag title={record.statusMessage}>{record.status}</Tag>
                            <Text >{moment(date).format(environment.defaultDateFormat)}</Text>
                        </Flex>
                    )
                },
            }
        );

        if (activeTab === "All" || activeTab === "Completed") {
            columns.push({
                title: <Text weight='500' noWrap>Last Payment</Text>,
                dataIndex: 'payments',
                key: 'payments',
                width: "140px",
                sorter: true,
                showSorterTooltip: false,
                align: 'right',
                render: (payments, bill) => {
                    var fPayments = (payments || []).filter(p => ["Completed"].includes(p.paymentStatus));
                    // console.log("fPayments", bill.invoiceNumber, fPayments, payments)
                    return (
                        (fPayments && fPayments.length > 0 && fPayments[fPayments.length - 1].paymentDate) ? 
                        <Flex start centerHorizontally gap='8px'>
                            {getDateInLocalTZ(fPayments[fPayments.length - 1].paymentDate, environment.defaultDateFormat)}
                        </Flex>
                        : "--"
                    )
                    
                }
            });
        }

        

        // if (activeTab !== "Scheduled") {
        //     columns.push({
        //         title: <Text weight='500' noWrap>Status</Text>,
        //         dataIndex: 'status',
        //         key: 'status',
        //         sorter: true,
        //         showSorterTooltip: false,
        //         render: (status, bill) => <Tag title={bill.statusMessage}>{status}</Tag>,
        //     });
        // }

        // columns.push({
        //     title: <Text>Last Updated</Text>,
        //     dataIndex: 'auditData',
        //     key: 'auditData',
        //     sorter: true,
        //     showSorterTooltip: false,
        //     render: auditData => moment(auditData.lastUpdatedTime).format('MM/DD/YY'),
        //     align: 'right',
        // });

        

        // Only add Action column if actions are enabled
        if (!hideAction) {
            columns.push({
                title: '',
                key: 'action',
                align: 'center',
                width: "40px",
                onHover: true,
                render: (text, bill) => {
                    return !["Paid", "Processing", "Initiated", "Pending Approval", "Partially Paid"].includes(bill.status) && 
                        (bill.status === "New" || bill.status === "Unpaid" || bill.status === "Canceled" || bill.status === "Initiated") ?
                        <Dropdown
                            placement='bottomRight'
                            trigger='click'
                            key={`${bill.invoiceNumber}-Dropdown`}
                            overlay={
                                <Menu style={{ padding: 24, borderRadius: 8, zIndex: 1001 }} key={`${bill.invoiceNumber}-Menu`} onClick={() => { }}>
                                    <FlexColumn gap='24px'>
                                        {
                                            (bill.status === "New" || bill.status === "Unpaid" || bill.status === "Canceled" || bill.status === "Initiated") &&
                                            <TextButton
                                                text='DELETE'
                                                rightIcon={<DeleteOutlined style={{ fontSize: 18 }} />}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    this.props.deleteBill(bill);
                                                }}
                                            />
                                        }
                                        {
                                            bill.status === "Scheduled" &&
                                            <TextButton text='RESCHEDULE' onClick={(e) => {
                                                e.stopPropagation();
                                                if (this.props.handleRescheduleBill) {
                                                    this.props.handleRescheduleBill(bill);
                                                }
                                            }} />
                                        }
                                    </FlexColumn>
                                </Menu>
                            }
                        >
                            <ImageButton src={More} />
                        </Dropdown>
                        : null;
                }
            });
        }

        // Filter out any columns specified in hideColumns
        if (hideColumns) {
            columns = columns.filter(column => !hideColumns.includes(column.key));
        }

        return columns;
    }

    render() {
        const { bills, loading, pagination, activeFilters } = this.state;
        const { 
            titleText = "Bills", 
            descText, 
            borderView, 
            ctaContent,
            activeTab,
            enableFiltering,
            vendorId,
        } = this.props;

        return (
            <TableView
                id={this.props.id || "bills-table"}
                tableLayout='auto'
                columns={this.getColumns()}
                dataSource={bills}
                rowKey='id'
                pagination={pagination}
                loading={loading || this.props.loading}
                onChange={this.handleTableChange}
                titleText={titleText}
                descText={descText}
                onRow={(record, rowIndex) => {
                    return {
                        onClick: event => {
                            this.handlePayBill(record);
                        },
                    };
                }}
                borderView={borderView}
                ctaContent={
                    <Flex between centerHorizontally fullWidth>
                        {enableFiltering && (
                            <SearchFilter
                                key={`${activeTab}-${vendorId}`}
                                onFilterChange={this.handleFilterChange}
                                onClearAll={this.clearAllFilters}
                                activeFilters={activeFilters}
                                showStatusFilter={activeTab === 'All' || !!vendorId}
                                hideSearch={vendorId}
                            />
                        )}
                        {ctaContent && (
                            <div>{ctaContent}</div>
                        )}
                    </Flex>
                }
            />
        );
    }
}

BillTable.defaultProps = {
    activeTab: "All",
    hideVendorColumn: false,
    hideAction: false,
    enableFiltering: false
};

function mapStateToProps(state) {
    return {
        aionStore: state.aionAppReducer,
        userDefaultsStore: state.userDefaultsReducer
    }
}

function mapDispatchToProps(dispatch) {
    return {
        dispatch
    }
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(withTheme(BillTable)));